import { useCallback, useEffect, useRef, useState } from "react";

export function formatPracticeTime(milliseconds) {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  const minutes = String(Math.floor(totalSeconds / 60) % 60).padStart(2, "0");
  const hours = Math.floor(totalSeconds / 3600);
  return hours > 0 ? `${String(hours).padStart(2, "0")}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
}

export default function usePracticeTimer() {
  const accumulated = useRef(0);
  const startedAt = useRef(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const readElapsed = useCallback(() => accumulated.current + (
    startedAt.current === null ? 0 : performance.now() - startedAt.current
  ), []);

  const start = useCallback(() => {
    if (startedAt.current !== null) return;
    startedAt.current = performance.now();
    setRunning(true);
  }, []);

  const stop = useCallback(() => {
    if (startedAt.current === null) return;
    accumulated.current = readElapsed();
    startedAt.current = null;
    setElapsed(accumulated.current);
    setRunning(false);
  }, [readElapsed]);

  const reset = useCallback(() => {
    accumulated.current = 0;
    if (startedAt.current !== null) startedAt.current = performance.now();
    setElapsed(0);
  }, []);

  useEffect(() => {
    if (!running) return;
    // This refreshes the display only; elapsed time always comes from timestamps.
    const interval = setInterval(() => setElapsed(readElapsed()), 1000);
    return () => clearInterval(interval);
  }, [running, readElapsed]);

  return { elapsed, start, stop, reset };
}
